import { IconArrowDown, IconArrowUp, IconPencil, IconPlus } from '@tabler/icons-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Sheet, Spinner, useToast } from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import type { ConsentMethod } from '@/types/api';
import { type ConsentMethodInput, consentsApi } from '../api';

const TEXT_KEYS = ['purpose', 'risks', 'alternatives', 'consequences'] as const;

function MethodForm({
  method,
  onSaved,
}: {
  method?: ConsentMethod;
  onSaved: (m: ConsentMethod) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [v, setV] = useState<ConsentMethodInput>({
    name: method?.name ?? '',
    purpose: method?.purpose ?? '',
    risks: method?.risks ?? '',
    alternatives: method?.alternatives ?? '',
    consequences: method?.consequences ?? '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const trimmed = Object.fromEntries(
        Object.entries(v).map(([k, x]) => [k, x.trim()])
      ) as ConsentMethodInput;
      onSaved(
        method
          ? await consentsApi.updateMethod(method.id, trimmed)
          : await consentsApi.createMethod(trimmed)
      );
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="stack" onSubmit={submit}>
      <div className="field">
        <label htmlFor="m-name">{t('consents.methods.name')}</label>
        <input
          id="m-name"
          className="input"
          required
          maxLength={200}
          value={v.name}
          onChange={(e) => setV((x) => ({ ...x, name: e.target.value }))}
        />
      </div>
      {TEXT_KEYS.map((key) => (
        <div className="field" key={key}>
          <label htmlFor={`m-${key}`}>{t(`consents.paper.${key}`)}</label>
          <textarea
            id={`m-${key}`}
            className="textarea"
            required
            rows={4}
            maxLength={4000}
            value={v[key]}
            onChange={(e) => setV((x) => ({ ...x, [key]: e.target.value }))}
          />
        </div>
      ))}
      <button type="submit" className="btn btn--dark btn--block" disabled={busy}>
        {t('common.save')}
      </button>
    </form>
  );
}

/**
 * The clinic's invasive methods and the texts their consents inform with.
 * Starts with the built-in list; a method is retired rather than deleted, and
 * consents already given keep the text they were given with.
 */
export function ConsentMethodsPanel() {
  const { t } = useTranslation();
  const toast = useToast();
  const methods = useResource(() => consentsApi.methods(true), 'consent-methods');
  const [editing, setEditing] = useState<ConsentMethod | 'new' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (methods.error) return <Banner tone="error">{errorMessage(methods.error)}</Banner>;
  if (!methods.data) return <Spinner />;
  const list = methods.data;

  const replace = (m: ConsentMethod) =>
    methods.setData((all) =>
      (all?.some((x) => x.id === m.id)
        ? all.map((x) => (x.id === m.id ? m : x))
        : [...(all ?? []), m]
      )
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
    );

  const run = async (id: string, action: () => Promise<void>) => {
    setBusy(id);
    try {
      await action();
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  };

  /** Swaps the list positions of the method at `i` and its neighbour. */
  const move = (i: number, by: -1 | 1) => {
    const a = list[i];
    const b = list[i + by];
    if (!(a && b)) return;
    void run(a.id, async () => {
      const [first, second] = await Promise.all([
        consentsApi.updateMethod(a.id, { sortOrder: b.sortOrder }),
        consentsApi.updateMethod(b.id, { sortOrder: a.sortOrder }),
      ]);
      replace(first);
      replace(second);
    });
  };

  return (
    <section className="card">
      <div className="card__head">
        <h2>{t('consents.methods.title')}</h2>
        <button type="button" className="btn btn--sm btn--dark" onClick={() => setEditing('new')}>
          <IconPlus size={18} /> {t('common.new')}
        </button>
      </div>
      <p className="small muted">{t('consents.methods.hint')}</p>
      <ul className="list">
        {list.map((m, i) => (
          <li key={m.id} className="user-row">
            <div className="grow">
              <strong>{m.name}</strong>
              {!m.active && <span className="badge">{t('consents.methods.retired')}</span>}
            </div>
            <button
              type="button"
              className="icon-btn icon-btn--sm"
              disabled={busy !== null || i === 0}
              onClick={() => move(i, -1)}
              aria-label={t('consents.methods.up', { name: m.name })}
            >
              <IconArrowUp size={16} />
            </button>
            <button
              type="button"
              className="icon-btn icon-btn--sm"
              disabled={busy !== null || i === list.length - 1}
              onClick={() => move(i, 1)}
              aria-label={t('consents.methods.down', { name: m.name })}
            >
              <IconArrowDown size={16} />
            </button>
            <button
              type="button"
              className="btn btn--sm"
              disabled={busy !== null}
              onClick={() =>
                run(m.id, async () =>
                  replace(await consentsApi.updateMethod(m.id, { active: !m.active }))
                )
              }
            >
              {t(m.active ? 'consents.methods.retire' : 'consents.methods.restore')}
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setEditing(m)}
              aria-label={t('consents.methods.edit', { name: m.name })}
              title={t('consents.methods.edit', { name: m.name })}
            >
              <IconPencil size={18} />
            </button>
          </li>
        ))}
      </ul>
      {editing && (
        <Sheet
          title={editing === 'new' ? t('consents.methods.new') : editing.name}
          onClose={() => setEditing(null)}
        >
          <MethodForm
            method={editing === 'new' ? undefined : editing}
            onSaved={(m) => {
              replace(m);
              setEditing(null);
            }}
          />
        </Sheet>
      )}
    </section>
  );
}
